(() => {
  if (customElements.get('swiper-carousel')) return;

  let swiperAvailabilityPromise = null;
  const supportedPaginationTypes = new Set(['bullets', 'fraction', 'progressbar']);

  function parsePositiveNumber(value) {
    if (typeof value !== 'string' || value.trim() === '') return null;

    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : null;
  }

  function parseNonNegativeNumber(value) {
    if (typeof value !== 'string' || value.trim() === '') return null;

    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) && parsedValue >= 0 ? parsedValue : null;
  }

  function waitForSwiperAvailability() {
    if (typeof window.Swiper === 'function') return Promise.resolve(true);
    if (document.readyState === 'complete') return Promise.resolve(false);
    if (swiperAvailabilityPromise) return swiperAvailabilityPromise;

    swiperAvailabilityPromise = new Promise((resolve) => {
      const finish = (isAvailable) => {
        document.removeEventListener('DOMContentLoaded', checkAvailability);
        window.removeEventListener('load', checkAvailability);
        resolve(isAvailable);
      };

      const checkAvailability = () => {
        if (typeof window.Swiper === 'function') {
          finish(true);
        } else if (document.readyState === 'complete') {
          finish(false);
        }
      };

      document.addEventListener('DOMContentLoaded', checkAvailability, { once: true });
      window.addEventListener('load', checkAvailability, { once: true });
    });

    return swiperAvailabilityPromise;
  }

  // Public events keep section-specific controllers decoupled from Swiper internals.
  class SwiperCarousel extends HTMLElement {
    constructor() {
      super();
      this.swiper = null;
      this.isInitializing = false;
      this.swiperRequest = null;
      this.handleSlideChange = this.handleSlideChange.bind(this);
    }

    connectedCallback() {
      if (this.swiper || this.isInitializing || this.swiperRequest) return;

      this.waitForSwiper();
    }

    disconnectedCallback() {
      this.swiperRequest = null;

      if (this.swiper && !this.swiper.destroyed) {
        const swiper = this.swiper;
        this.dispatchEvent(
          new CustomEvent('swiper:before-destroy', {
            bubbles: true,
            detail: { swiper },
          })
        );
        swiper.off('slideChange', this.handleSlideChange);
        swiper.destroy(true, true);
      }

      this.swiper = null;
      this.isInitializing = false;
    }

    waitForSwiper() {
      const request = {};
      this.swiperRequest = request;

      waitForSwiperAvailability().then((isAvailable) => {
        if (this.swiperRequest !== request || !this.isConnected) return;

        this.swiperRequest = null;
        if (isAvailable) {
          this.initialize();
        } else {
          this.reportMissingSwiper();
        }
      });
    }

    reportMissingSwiper() {
      console.warn('swiper-carousel: window.Swiper is not available.');
    }

    initialize() {
      if (this.swiper || this.isInitializing || !this.isConnected) return;

      const slider = this.querySelector(':scope > [data-swiper-container]');
      if (!slider) return;

      const wrapper = slider.querySelector(':scope > .swiper-wrapper');
      if (!wrapper) return;

      const slideCount = Array.from(wrapper.children).filter((element) =>
        element.classList.contains('swiper-slide')
      ).length;
      if (slideCount === 0) return;

      const nextEl = this.querySelector('[data-swiper-next]');
      const prevEl = this.querySelector('[data-swiper-prev]');
      const paginationEl = this.querySelector('[data-swiper-pagination]');
      const autoplayEnabled = this.dataset.autoplay === 'true' && slideCount > 1;
      const parsedAutoplayDelay = Number(this.dataset.autoplayDelay);
      const autoplayDelay =
        Number.isFinite(parsedAutoplayDelay) && parsedAutoplayDelay > 0 ? parsedAutoplayDelay : 5000;
      const slidesMobile = parsePositiveNumber(this.dataset.slidesMobile);
      const spaceMobile = parseNonNegativeNumber(this.dataset.spaceMobile);
      const breakpoint = parsePositiveNumber(this.dataset.breakpoint);
      const slidesDesktop = parsePositiveNumber(this.dataset.slidesDesktop);
      const spaceDesktop = parseNonNegativeNumber(this.dataset.spaceDesktop);
      const requestedPaginationType = this.dataset.paginationType;
      const paginationType = supportedPaginationTypes.has(requestedPaginationType)
        ? requestedPaginationType
        : null;

      const config = {
        loop: this.dataset.loop !== 'false' && slideCount > 1,
      };

      if (slidesMobile !== null) {
        config.slidesPerView = slidesMobile;
      }

      if (spaceMobile !== null) {
        config.spaceBetween = spaceMobile;
      }

      if (breakpoint !== null && (slidesDesktop !== null || spaceDesktop !== null)) {
        const breakpointConfig = {};

        if (slidesDesktop !== null) {
          breakpointConfig.slidesPerView = slidesDesktop;
        }

        if (spaceDesktop !== null) {
          breakpointConfig.spaceBetween = spaceDesktop;
        }

        config.breakpoints = {
          [breakpoint]: breakpointConfig,
        };
      }

      if (nextEl && prevEl) {
        config.navigation = { nextEl, prevEl };
      }

      if (paginationEl) {
        config.pagination = {
          el: paginationEl,
          clickable: paginationType === null || paginationType === 'bullets',
        };

        if (paginationType !== null) {
          config.pagination.type = paginationType;
        }
      }

      if (autoplayEnabled) {
        config.autoplay = {
          delay: autoplayDelay,
          disableOnInteraction: false,
        };
      }

      this.isInitializing = true;
      let swiper = null;

      try {
        swiper = new window.Swiper(slider, config);
        swiper.on('slideChange', this.handleSlideChange);
        this.swiper = swiper;
      } catch (error) {
        if (swiper && !swiper.destroyed && typeof swiper.destroy === 'function') {
          try {
            swiper.destroy(true, true);
          } catch (cleanupError) {
            console.error('swiper-carousel: failed to clean up after initialization error.', cleanupError);
          }
        }

        this.swiper = null;
        console.error('swiper-carousel: failed to initialize.', error);
        throw error;
      } finally {
        this.isInitializing = false;
      }

      this.dispatchEvent(
        new CustomEvent('swiper:ready', {
          bubbles: true,
          detail: { swiper: this.swiper },
        })
      );
    }

    handleSlideChange() {
      this.dispatchEvent(
        new CustomEvent('swiper:slide-change', {
          bubbles: true,
          detail: { swiper: this.swiper },
        })
      );
    }
  }

  customElements.define('swiper-carousel', SwiperCarousel);
})();
